import React, { useEffect, useMemo, useState } from 'react';

import { Form } from 'antd';
import classNames from 'classnames';
import styled from 'styled-components';

import { CMASummaryContractsFilterContext } from 'components/CMASummary/filters/CMASummaryContractsFilter/CMASummaryContractsFilterContext';
import CMASummaryContractsFilterLabel from 'components/CMASummary/filters/CMASummaryContractsFilter/CMASummaryContractsFilterLabel';
import CMASummaryFilterContractDisplay from 'components/CMASummary/filters/CMASummaryContractsFilter/CMASummaryFilterContractDisplay';
import CMASummaryFilterContractSelection from 'components/CMASummary/filters/CMASummaryContractsFilter/CMASummaryFilterContractSelection';
import CMASummarySelect from 'components/CMASummary/filters/CMASummarySelect';
import FilterDropdownHeader from 'components/lib/FilterDropdownHeader';
import { useCommunityViewContext } from 'components/View/CommunityViewContext';
import { ViewFilters } from 'components/View/types';

import { COMMUNITY_CONTRACT_SELECTION_TYPE, CONTRACT_DISPLAY_TYPES } from '../../constants';

const DropdownContainer = styled.div`
  display: flex;
  flex-direction: column;

  .ant-form-item .ant-form-item-control-input {
    min-height: 0;
  }
`;

const ContractTypeFilter = () => {
  const [open, setOpen] = useState(false);
  const [form] = Form.useForm();

  const { community, viewFilters, setViewFilters } = useCommunityViewContext();
  const filters = (viewFilters || {}) as ViewFilters;

  const resolvedContractDisplayType = CONTRACT_DISPLAY_TYPES.MY_CONTRACTS;
  const resolvedContractSelectionType =
    filters.communityContractSelectionType ?? COMMUNITY_CONTRACT_SELECTION_TYPE.APPROXIMATE;

  const selectedSlugFromFilters = filters.communityContractSlugs?.[0];
  const selectedContract = useMemo(() => {
    if (!community?.contracts) {
      return undefined;
    }
    if (selectedSlugFromFilters) {
      return community.contracts.find((contract: any) => contract.slug === selectedSlugFromFilters);
    }
    if (filters.communityContractType) {
      return community.contracts.find((contract: any) => contract.id === filters.communityContractType);
    }
    return undefined;
  }, [community?.contracts, filters.communityContractType, selectedSlugFromFilters]);

  const selectedSlug = selectedContract?.slug;

  const initialValues = useMemo(
    () => ({
      contractDisplayType: resolvedContractDisplayType,
      contractSelectionType: resolvedContractSelectionType,
      communityContractSlugs: selectedSlug ? [selectedSlug] : [],
    }),
    [resolvedContractDisplayType, resolvedContractSelectionType, selectedSlug]
  );

  useEffect(() => {
    form.setFieldsValue(initialValues);
  }, [form, initialValues]);

  const handleSubmit = (values: any) => {
    const slug = values?.communityContractSlugs?.[0];

    const contract =
      community?.contracts?.find((communityContract: any) => communityContract.slug === slug) ?? undefined;

    setViewFilters((prevState: any) => ({
      ...prevState,
      communityContractSelectionType: values.contractSelectionType,
      communityContractSlugs: slug ? [slug] : [],
      communityContractType: contract?.id,
      contractTypes: contract?.contract_id ? [contract.contract_id] : [],
    }));
    setOpen(false);
  };

  const handleReset = () => {
    form.setFieldsValue({
      contractSelectionType: COMMUNITY_CONTRACT_SELECTION_TYPE.APPROXIMATE,
      communityContractSlugs: [],
    });

    setViewFilters((prevState: any) => ({
      ...prevState,
      communityContractSelectionType: COMMUNITY_CONTRACT_SELECTION_TYPE.APPROXIMATE,
      communityContractSlugs: [],
      communityContractType: undefined,
      contractTypes: [],
    }));
  };

  const contextValue = useMemo(
    () => ({
      form,
      contractDisplayType: resolvedContractDisplayType,
      contractSelectionType: resolvedContractSelectionType,
      communityContractSlugs: selectedSlug ? [selectedSlug] : [],
    }),
    [form, resolvedContractDisplayType, resolvedContractSelectionType, selectedSlug]
  );

  return (
    <CMASummarySelect
      open={open}
      onDropdownVisibleChange={(visible) => setOpen(visible)}
      className={classNames({ active: Boolean(selectedSlug) })}
      dropdownStyle={{ padding: 0, borderRadius: 0, border: '1px solid var(--line-gray)', minWidth: 420 }}
      dropdownRender={() => (
        <Form form={form} initialValues={initialValues} onFinish={handleSubmit}>
          <CMASummaryContractsFilterContext.Provider value={contextValue}>
            <DropdownContainer onClick={(event) => event.stopPropagation()}>
              <FilterDropdownHeader onReset={handleReset} />
              <CMASummaryFilterContractDisplay showEveryoneOption={false} />
              <Form.Item name="communityContractSlugs" noStyle>
                <CMASummaryFilterContractSelection maxSelectable={1} forceDisableSelectAll hideSelectAll />
              </Form.Item>
            </DropdownContainer>
          </CMASummaryContractsFilterContext.Provider>
        </Form>
      )}
      labelRender={() => (
        <CMASummaryContractsFilterLabel
          contractDisplayType={resolvedContractDisplayType}
          contractSelectionType={resolvedContractSelectionType}
        />
      )}
      value={selectedSlug ?? ''}
    />
  );
};

export default ContractTypeFilter;
